import api, { handleResponse, handleError } from './api';
import { UPLOAD_BASE } from '../utils/apiBase';
import uploadVideoResumable from '../utils/tusUpload';

/** The most a clip may weigh on the wire; the server refuses more. */
/** A video message: 2 GB on the wire, 15 minutes after the trim (handoff §4b). Portals keep 30 s. */
export const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;
export const MAX_MESSAGE_CLIP_MS = 15 * 60 * 1000;

const messageService = {
  async getThreads(pageId = null) {
    try {
      return handleResponse(await api.get(`/messages/threads${pageId ? `?pageId=${pageId}` : ''}`));
    } catch (e) { return handleError(e); }
  },

  async getThread(threadId) {
    try {
      return handleResponse(await api.get(`/messages/threads/${threadId}`));
    } catch (e) { return handleError(e); }
  },

  // replyToId: the story / Portal this answers, so a reply to someone who isn't a friend yet
  // still opens a chat (new chats otherwise need a friendship, Nov 2026).
  async createOrGetThread(peerUserId, eventId = null, asProBusinessId = null, replyToId = null) {
    try {
      return handleResponse(await api.post('/messages/threads', { peerUserId, eventId, asProBusinessId, ...(replyToId ? { replyToId } : {}) }));
    } catch (e) { return handleError(e); }
  },

  async addParticipants(threadId, userIds) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/participants`, { userIds }));
    } catch (e) { return handleError(e); }
  },

  async startConversation(userIds, asProBusinessId = null) {
    try {
      return handleResponse(await api.post('/messages/threads/group', { userIds, asProBusinessId }));
    } catch (e) { return handleError(e); }
  },

  async sendMessage(threadId, payload) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/messages`, payload));
    } catch (e) { return handleError(e); }
  },

  async sendAudio(threadId, blob, durationSec, peaks = null) {
    try {
      const fd = new FormData();
      // The waveform the recorder measured, one base-36 character per bar.
      if (peaks) fd.append('peaks', peaks);
      // The extension follows the blob's own type: the recorder produces webm
      // on Chrome and mp4 on Safari, and the server derives the stored
      // extension from the content type it receives.
      const ext = (blob.type || '').includes('mp4') ? 'm4a' : (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
      fd.append('file', blob, `voice.${ext}`);
      fd.append('durationSec', String(Math.max(0, Math.round(durationSec || 0))));
      return handleResponse(await api.post(`/messages/threads/${threadId}/audio`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }));
    } catch (e) { return handleError(e); }
  },

  async sendImage(threadId, file, { viewOnce = false, viewLimit = 1 } = {}) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (viewOnce) { fd.append('viewOnce', 'true'); fd.append('viewLimit', String(viewLimit)); }
      return handleResponse(await api.post(`/messages/threads/${threadId}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }));
    } catch (e) { return handleError(e); }
  },

  /**
   * A clip. Resumable, in chunks, through our API to Cloudflare (`utils/tusUpload.js`): a 2 GB
   * file never sits on the box and a dropped connection resumes. `trim` is `{ startMs, endMs }`
   * in the uploaded file's clock — the device cut rebases it when it manages one. The finish
   * call turns the upload into the message.
   */
  async sendVideo(threadId, file, { trim = null, viewOnce = false, viewLimit = 1, onProgress = null, signal = null } = {}) {
    try {
      const { uploadId } = await uploadVideoResumable({ threadId, file, onProgress, signal });
      const body = { viewOnce, viewLimit: viewOnce ? viewLimit : 1 };
      if (trim && trim.endMs > trim.startMs) { body.trimStartMs = Math.round(trim.startMs); body.trimEndMs = Math.round(trim.endMs); }
      return handleResponse(await api.post(`/messages/video/uploads/${uploadId}/finish`, body));
    } catch (e) {
      if (e?.isNetwork !== undefined) return { success: false, error: e.message, isNetwork: !!e.isNetwork };
      return handleError(e);
    }
  },

  /** The one-shot upload, kept for a file the resumable path can't take (tests, old shells). */
  async sendVideoOneShot(threadId, file, { trim = null, viewOnce = false, viewLimit = 1, onProgress = null } = {}) {
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'clip.mp4');
      if (trim && trim.endMs > trim.startMs) { fd.append('trimStartMs', String(Math.round(trim.startMs))); fd.append('trimEndMs', String(Math.round(trim.endMs))); }
      if (viewOnce) { fd.append('viewOnce', 'true'); fd.append('viewLimit', String(viewLimit)); }
      return handleResponse(await api.post(`/messages/threads/${threadId}/video`, fd, {
        baseURL: UPLOAD_BASE,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 15 * 60 * 1000,
        onUploadProgress: onProgress ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0) : undefined,
      }));
    } catch (e) { return handleError(e); }
  },

  // ── Albums (Oct 2026) ──────────────────────────────────────────────────
  // Each item goes up on its own (the outbox runs three at once); the album is then sent as
  // the ordered list of item ids. Photos are converted and stored at once; clips go through
  // UPLOAD_BASE to Cloudflare like a video message and come back `encoding`.

  async stageAlbumImage(threadId, file, { onProgress = null } = {}) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      return handleResponse(await api.post(`/messages/threads/${threadId}/album/items/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0) : undefined,
      }));
    } catch (e) { return handleError(e); }
  },

  async stageAlbumVideo(threadId, file, { trim = null, onProgress = null, signal = null } = {}) {
    try {
      const { uploadId } = await uploadVideoResumable({ threadId, file, onProgress, signal });
      const body = {};
      if (trim && trim.endMs > trim.startMs) { body.trimStartMs = Math.round(trim.startMs); body.trimEndMs = Math.round(trim.endMs); }
      return handleResponse(await api.post(`/messages/video/uploads/${uploadId}/finish-album-item`, body));
    } catch (e) {
      if (e?.isNetwork !== undefined) return { success: false, error: e.message, isNetwork: !!e.isNetwork };
      return handleError(e);
    }
  },

  /** The one-shot album clip upload, kept for older shells. */
  async stageAlbumVideoOneShot(threadId, file, { trim = null, onProgress = null } = {}) {
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'clip.mp4');
      if (trim && trim.endMs > trim.startMs) { fd.append('trimStartMs', String(Math.round(trim.startMs))); fd.append('trimEndMs', String(Math.round(trim.endMs))); }
      return handleResponse(await api.post(`/messages/threads/${threadId}/album/items/video`, fd, {
        baseURL: UPLOAD_BASE,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 15 * 60 * 1000,
        onUploadProgress: onProgress ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0) : undefined,
      }));
    } catch (e) { return handleError(e); }
  },

  async sendAlbum(threadId, { itemIds, viewOnce = false, viewLimit = 1 }) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/album`, { itemIds, viewOnce, viewLimit: viewOnce ? viewLimit : 1 }));
    } catch (e) { return handleError(e); }
  },

  // ── Chat info (Oct 2026) ───────────────────────────────────────────────
  async getThreadInfo(threadId) {
    try { return handleResponse(await api.get(`/messages/threads/${threadId}/info`)); } catch (e) { return handleError(e); }
  },
  async getThreadMedia(threadId) {
    try { return handleResponse(await api.get(`/messages/threads/${threadId}/media`)); } catch (e) { return handleError(e); }
  },
  async setThreadMuted(threadId, muted) {
    try { return handleResponse(await api.put(`/messages/threads/${threadId}/mute`, { muted })); } catch (e) { return handleError(e); }
  },

  // ── Calls (Oct 2026): the ICE servers for one call, and the record a call leaves behind ──
  async getIceServers() {
    try { return handleResponse(await api.get('/messages/calls/ice')); } catch (e) { return handleError(e); }
  },

  async postCallRecord(threadId, { callId, video, status, durationSec = 0, people = 2 }) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/calls/record`, { callId, video, status, durationSec, people }));
    } catch (e) { return handleError(e); }
  },

  async markRead(threadId) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/read`));
    } catch (e) { return handleError(e); }
  },

  // Mark a conversation as unread for the current user.
  async markUnread(threadId) {
    try {
      return handleResponse(await api.post(`/messages/threads/${threadId}/unread`));
    } catch (e) { return handleError(e); }
  },

  // Toggle an emoji reaction on a message. Returns the updated message.
  async react(messageId, emoji) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/react`, { emoji }));
    } catch (e) { return handleError(e); }
  },

  // ── Secure chats (Oct 2026) ─────────────────────────────────────────────
  // A thread behind the viewer's own PIN / pattern. Per person: the other side's
  // copy is untouched. The lock and its attempt counter live on the server.

  async setThreadSecure(threadId, secure) {
    try {
      return handleResponse(await api.put(`/messages/threads/${threadId}/secure`, { secure }));
    } catch (e) { return handleError(e); }
  },

  async getSecureLock() {
    try {
      return handleResponse(await api.get('/messages/secure/lock'));
    } catch (e) { return handleError(e); }
  },

  // Set the first lock, or change it from inside the open vault.
  async setSecureLock(type, code) {
    try {
      return handleResponse(await api.put('/messages/secure/lock', { type, code }));
    } catch (e) { return handleError(e); }
  },

  // { ok, triesLeft, lockedUntil, status }
  async secureUnlock(code) {
    try {
      return handleResponse(await api.post('/messages/secure/unlock', { code }));
    } catch (e) { return handleError(e); }
  },

  async secureLockNow() {
    try {
      return handleResponse(await api.post('/messages/secure/lock-now'));
    } catch (e) { return handleError(e); }
  },

  // FORGOT: the account password clears the lock so a new one can be set.
  async secureReset(password) {
    try {
      return handleResponse(await api.post('/messages/secure/lock/reset', { password }));
    } catch (e) { return handleError(e); }
  },

  // Web biometrics: a WebAuthn platform credential, verified on the server.
  async passkeyRegisterOptions() {
    try { return handleResponse(await api.post('/messages/secure/passkeys/register/options')); } catch (e) { return handleError(e); }
  },
  async passkeyRegister(credential, label) {
    try { return handleResponse(await api.post('/messages/secure/passkeys/register', { credential, label })); } catch (e) { return handleError(e); }
  },
  async passkeyAssertOptions() {
    try { return handleResponse(await api.post('/messages/secure/passkeys/assert/options')); } catch (e) { return handleError(e); }
  },
  async passkeyAssert(credential) {
    try { return handleResponse(await api.post('/messages/secure/passkeys/assert', { credential })); } catch (e) { return handleError(e); }
  },
  async passkeyList() {
    try { return handleResponse(await api.get('/messages/secure/passkeys')); } catch (e) { return handleError(e); }
  },
  async passkeyDelete(id) {
    try { return handleResponse(await api.delete(`/messages/secure/passkeys/${id}`)); } catch (e) { return handleError(e); }
  },

  async setSecureWindow(windowMinutes) {
    try {
      return handleResponse(await api.put('/messages/secure/window', { windowMinutes }));
    } catch (e) { return handleError(e); }
  },

  // Delete a conversation for the current user only.
  async deleteThread(threadId) {
    try {
      return handleResponse(await api.delete(`/messages/threads/${threadId}`));
    } catch (e) { return handleError(e); }
  },

  // Unsend a message (sender only) — removes it for everyone.
  async unsendMessage(messageId) {
    try {
      return handleResponse(await api.delete(`/messages/messages/${messageId}`));
    } catch (e) { return handleError(e); }
  },

  // Edit a text message (sender only, within 20 minutes).
  async editMessage(messageId, body) {
    try {
      return handleResponse(await api.put(`/messages/messages/${messageId}`, { body }));
    } catch (e) { return handleError(e); }
  },

  // ── View once: the one look, and the device's word that a screenshot happened ──
  async openViewOnce(messageId) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/view-once/open`));
    } catch (e) { return handleError(e); }
  },

  async reportViewOnceScreenshot(messageId) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/view-once/screenshot`));
    } catch (e) { return handleError(e); }
  },

  // ── Voice-note player: the listener's private notes + what they've heard ──
  /** The dock's WHOLE CHAT search: every readable note across the thread's voice notes, grouped. */
  async getThreadVoiceNotes(threadId, { q = '', who = 'all' } = {}) {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (who && who !== 'all') params.set('who', who === 'marks' ? 'bookmarks' : who);
      const qs = params.toString();
      return handleResponse(await api.get(`/messages/threads/${threadId}/voice-notes/notes${qs ? `?${qs}` : ''}`));
    } catch (e) { return handleError(e); }
  },

  async getVoiceNote(messageId) {
    try {
      return handleResponse(await api.get(`/messages/messages/${messageId}/voice`));
    } catch (e) { return handleError(e); }
  },

  // `shared` is the composer's switch: a note is the writer's own unless it says otherwise.
  async addVoiceNoteNote(messageId, atSec, body, shared = false) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/voice/notes`, { atSec, body, shared }));
    } catch (e) { return handleError(e); }
  },

  // Reply to a note you can read — yours, or one someone shared. 500 characters, plain text.
  async addVoiceNoteReply(messageId, noteId, body) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/voice/notes/${noteId}/replies`, { body }));
    } catch (e) { return handleError(e); }
  },

  async deleteVoiceNoteReply(messageId, noteId, replyId) {
    try {
      return handleResponse(await api.delete(`/messages/messages/${messageId}/voice/notes/${noteId}/replies/${replyId}`));
    } catch (e) { return handleError(e); }
  },

  async deleteVoiceNoteNote(messageId, noteId) {
    try {
      return handleResponse(await api.delete(`/messages/messages/${messageId}/voice/notes/${noteId}`));
    } catch (e) { return handleError(e); }
  },

  // Share one of your notes with the thread, or take it back.
  async setVoiceNoteNoteShared(messageId, noteId, shared) {
    try {
      return handleResponse(await api.patch(`/messages/messages/${messageId}/voice/notes/${noteId}`, { shared }));
    } catch (e) { return handleError(e); }
  },

  // A note sent without a waveform: keep the one this browser measured.
  async saveVoicePeaks(messageId, peaks) {
    try {
      return handleResponse(await api.put(`/messages/messages/${messageId}/voice/peaks`, { peaks }));
    } catch (e) { return handleError(e); }
  },

  // The note's bytes (the CDN has no CORS, so decoding goes through the API).
  async getVoiceAudio(messageId) {
    try {
      const r = await api.get(`/messages/messages/${messageId}/voice/audio`, { responseType: 'arraybuffer' });
      return { success: true, data: r.data };
    } catch (e) { return handleError(e); }
  },

  async saveVoiceListened(messageId, ranges) {
    try {
      return handleResponse(await api.put(`/messages/messages/${messageId}/voice/listened`, { ranges }));
    } catch (e) { return handleError(e); }
  },

  // The written form of a voice note (any thread member). Poll while status is pending.
  async getVoiceTranscript(messageId) {
    try {
      return handleResponse(await api.get(`/messages/messages/${messageId}/transcript`));
    } catch (e) { return handleError(e); }
  },

  async retryVoiceTranscript(messageId) {
    try {
      return handleResponse(await api.post(`/messages/messages/${messageId}/transcript/retry`));
    } catch (e) { return handleError(e); }
  },

  // You + every Pro page you manage, each with its unread count (the switcher at the top of MESSAGES).
  // ── Plan detection (Oct 2026) ──────────────────────────────────────────
  // One private suggestion per chat; the account switch, the per-chat switch, FIND A PLAN,
  // × (24 h quiet) and UNDO, and the card posted once the viewer publishes.
  async getPlanDetection() {
    try { return handleResponse(await api.get('/messages/plan-detection')); } catch (e) { return handleError(e); }
  },
  async setPlanDetection(enabled) {
    try { return handleResponse(await api.put('/messages/plan-detection', { enabled })); } catch (e) { return handleError(e); }
  },
  async setThreadPlanDetection(threadId, enabled) {
    try { return handleResponse(await api.put(`/messages/threads/${threadId}/plan-detection`, { enabled })); } catch (e) { return handleError(e); }
  },
  /** The viewer's suggestion for a chat, or `data: null`. 423 behind a shut secure lock. */
  async getPlan(threadId) {
    try { return handleResponse(await api.get(`/messages/threads/${threadId}/plan`)); } catch (e) { return handleError(e); }
  },
  /** FIND A PLAN: `range` is messages · hour · today. `data: null` when nothing was found. */
  async findPlan(threadId, { range = 'messages', timeZone = null, lat = null, lng = null } = {}) {
    try { return handleResponse(await api.post(`/messages/threads/${threadId}/plan/find`, { range, timeZone, lat, lng })); } catch (e) { return handleError(e); }
  },
  async dismissPlan(threadId) {
    try { return handleResponse(await api.post(`/messages/threads/${threadId}/plan/dismiss`)); } catch (e) { return handleError(e); }
  },
  async undoDismissPlan(threadId) {
    try { return handleResponse(await api.post(`/messages/threads/${threadId}/plan/undo`)); } catch (e) { return handleError(e); }
  },
  /** The viewer published from the suggestion: post the card (kind portal | event). */
  async planPublished(threadId, kind, eventId) {
    try { return handleResponse(await api.post(`/messages/threads/${threadId}/plan/published`, { kind, eventId })); } catch (e) { return handleError(e); }
  },

  async getInboxes() {
    try {
      return handleResponse(await api.get('/messages/inboxes'));
    } catch (e) { return handleError(e); }
  },

  async getUnreadCount() {
    try {
      return handleResponse(await api.get('/messages/unread-count'));
    } catch (e) { return handleError(e); }
  },

  async broadcastToEvent(eventId, body, allowReplies, mode = 'fan_out') {
    try {
      return handleResponse(await api.post(`/messages/events/${eventId}/broadcast`, { body, allowReplies, mode }));
    } catch (e) { return handleError(e); }
  },

  // Host opens the event/signal group chat (creates it if needed) without posting.
  // Returns { threadId } to navigate to.
  async openEventGroup(eventId) {
    try {
      return handleResponse(await api.post(`/messages/events/${eventId}/group`));
    } catch (e) { return handleError(e); }
  },

  // "Message host" on a signal — resolves to the group room if the host opened
  // one (else the idempotent 1:1 host thread). Returns { threadId }.
  async resolveSignalHostThread(eventId) {
    try {
      return handleResponse(await api.post(`/messages/events/${eventId}/host-thread`));
    } catch (e) { return handleError(e); }
  },
};

export default messageService;
